import { useCallback, useEffect, useState } from "react";
import QRCode from "react-qr-code";
import { T, base } from "../lib/theme";
import { timeAgo, cleanPhone } from "../lib/utils";
import { Badge, Btn, Card, Icon } from "../components";
import { apiFetch } from "../lib/api";

// ── Date filter presets ───────────────────────────────────────────
const DATE_PRESETS = [
  { label: "All time",     value: "all"  },
  { label: "Today",        value: "today"},
  { label: "Yesterday",    value: "yesterday"},
  { label: "Last 3 days",  value: "3d"  },
  { label: "Last 7 days",  value: "7d"  },
  { label: "Last 30 days", value: "30d" },
  { label: "Last 3 months",value: "3m"  },
  { label: "Last year",    value: "1y"  },
];
function presetToRange(preset) {
  const now = new Date();
  const sod = new Date(now); sod.setHours(0,0,0,0);
  const eod = new Date(now); eod.setHours(23,59,59,999);
  switch (preset) {
    case "today":     return { dateFrom: sod.toISOString(), dateTo: eod.toISOString() };
    case "yesterday": { const yS = new Date(sod); yS.setDate(yS.getDate()-1); const yE = new Date(eod); yE.setDate(yE.getDate()-1); return { dateFrom: yS.toISOString(), dateTo: yE.toISOString() }; }
    case "3d":  { const s = new Date(sod); s.setDate(s.getDate()-2);  return { dateFrom: s.toISOString(), dateTo: eod.toISOString() }; }
    case "7d":  { const s = new Date(sod); s.setDate(s.getDate()-6);  return { dateFrom: s.toISOString(), dateTo: eod.toISOString() }; }
    case "30d": { const s = new Date(sod); s.setDate(s.getDate()-29); return { dateFrom: s.toISOString(), dateTo: eod.toISOString() }; }
    case "3m":  { const s = new Date(sod); s.setMonth(s.getMonth()-3);    return { dateFrom: s.toISOString(), dateTo: eod.toISOString() }; }
    case "1y":  { const s = new Date(sod); s.setFullYear(s.getFullYear()-1); return { dateFrom: s.toISOString(), dateTo: eod.toISOString() }; }
    default: return {};
  }
}

// Live uptime clock — updates every second
const UptimeClock = ({ connectedAt }) => {
  const [secs, setSecs] = useState(0);

  useEffect(() => {
    if (!connectedAt) return;
    const calc = () => {
      const diff = Math.floor((Date.now() - new Date(connectedAt).getTime()) / 1000);
      setSecs(Math.max(0, diff));
    };
    calc();
    const t = setInterval(calc, 1000);
    return () => clearInterval(t);
  }, [connectedAt]);

  const d  = Math.floor(secs / 86400);
  const h  = Math.floor((secs % 86400) / 3600);
  const m  = Math.floor((secs % 3600) / 60);
  const s  = secs % 60;
  const pad = (n) => String(n).padStart(2, "0");

  if (d > 0) return <span style={{ fontFamily: base.mono }}>{d}d {pad(h)}h {pad(m)}m</span>;
  return <span style={{ fontFamily: base.mono }}>{pad(h)}:{pad(m)}:{pad(s)}</span>;
};

// ── Connection card — shared between both modes ───────────────────
const ConnectionCard = ({ status, qr, sessionInfo, onShowModal, onDisconnect }) => {
  const sc = status === "connected" ? "emerald" : (status === "connecting" || status === "qr") ? "amber" : "rose";
  return (
    <Card>
      <div style={{ fontSize: 13, fontWeight: 600, color: T.text1, marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
        <Icon name="wifi" size={15} color={T.text3} /> WhatsApp
        <Badge label={status.replace(/_/g, " ").toUpperCase()} color={sc} />
      </div>

      {status === "connected" && sessionInfo?.connectedAt && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
          <div style={{ padding: "12px 14px", borderRadius: 12, background: T.emeraldDim, border: "1px solid rgba(99,102,241,0.2)" }}>
            <div style={{ fontSize: 10, color: T.emerald, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 4 }}>Uptime</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: T.emerald }}><UptimeClock connectedAt={sessionInfo.connectedAt} /></div>
          </div>
          <div style={{ padding: "12px 14px", borderRadius: 12, background: T.skyDim, border: "1px solid rgba(56,189,248,0.2)" }}>
            <div style={{ fontSize: 10, color: T.sky, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 4 }}>Number</div>
            <div style={{ fontSize: 14, fontWeight: 800, color: T.sky, fontFamily: base.mono }}>{sessionInfo.phone ? `+${sessionInfo.phone}` : "—"}</div>
          </div>
        </div>
      )}

      {qr && status === "qr" && (
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <div style={{ fontSize: 12, color: T.text3, marginBottom: 12, lineHeight: 1.6 }}>
            Open WhatsApp → <strong style={{ color: T.text2 }}>Linked Devices</strong> → <strong style={{ color: T.text2 }}>Link a Device</strong> → Scan
          </div>
          <div style={{ background: "#fff", padding: 14, borderRadius: 16, display: "inline-block", boxShadow: "0 12px 40px rgba(0,0,0,0.4)" }}>
            <QRCode value={qr} size={188} />
          </div>
          <div style={{ marginTop: 10, fontSize: 11, color: T.text3 }}>QR expires in ~60 seconds</div>
        </div>
      )}

      {status === "connecting" && (
        <div style={{ textAlign: "center", padding: "20px 0", marginBottom: 16 }}>
          <div style={{ width: 36, height: 36, border: `2px solid ${T.border2}`, borderTopColor: T.emerald, borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 14px" }} />
          <div style={{ fontSize: 13, color: T.text3 }}>Connecting to WhatsApp servers...</div>
        </div>
      )}

      {(status === "disconnected" || status === "logged_out") && (
        <div style={{ padding: "12px 14px", borderRadius: 12, background: T.roseDim, border: `1px solid rgba(244,63,94,0.2)`, fontSize: 13, color: T.rose, marginBottom: 16, lineHeight: 1.6 }}>
          Bot is offline — not replying to any messages
        </div>
      )}

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        {(status === "disconnected" || status === "logged_out") && (
          <Btn variant="primary" onClick={onShowModal}><Icon name="wifi" size={14} color="#fff" /> Connect WhatsApp</Btn>
        )}
        {(status === "qr" || status === "connecting") && (
          <Btn variant="outline" onClick={onShowModal}><Icon name="arrowLeft" size={14} /> Back to connection options</Btn>
        )}
        {status === "connected" && (
          <Btn variant="danger" onClick={onDisconnect}>Disconnect</Btn>
        )}
      </div>
    </Card>
  );
};

// ── Stat card helper ─────────────────────────────────────────────
const StatCard = ({ value, label, color, svg, delay = 0 }) => (
  <div style={{ background: T.bg2, border: `1px solid ${T.border1}`, borderRadius: 16, padding: "20px 22px", animation: `fadeUp 0.35s ease ${delay}s both` }}>
    <div style={{ width: 36, height: 36, borderRadius: 10, background: `${color}14`, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 12 }}>{svg}</div>
    <div style={{ fontSize: 30, fontWeight: 800, color, lineHeight: 1, fontFamily: base.mono }}>{value}</div>
    <div style={{ fontSize: 12, color: T.text3, marginTop: 6, fontWeight: 500 }}>{label}</div>
  </div>
);

// ── Pipeline funnel bar ───────────────────────────────────────────
const FunnelBar = ({ label, count, total, color }) => {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginBottom: 4 }}>
        <span style={{ color: T.text2 }}>{label}</span>
        <span style={{ fontFamily: base.mono, color }}>{count} <span style={{ color: T.text3 }}>({pct}%)</span></span>
      </div>
      <div style={{ height: 5, borderRadius: 99, background: T.border1, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${pct}%`, background: color, borderRadius: 99 }} />
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────
// LEAD GEN MODE — Overview
// ─────────────────────────────────────────────────────────────────
const LeadGenOverview = ({ overviewStats, chats, broadcasts, status, qr, sessionInfo, onShowModal, onDisconnect }) => {
  const s = overviewStats || {};
  const stages = s.stages || {};

  const statCards = [
    {
      value: s.totalContacts ?? chats.length,
      label: "Total Contacts",
      color: T.sky,
      svg: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={T.sky} strokeWidth="1.8"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
    },
    {
      value: s.hotLeads ?? 0,
      label: "Hot Leads",
      color: T.rose,
      svg: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={T.rose} strokeWidth="1.8"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z"/></svg>,
    },
    {
      value: s.myClients ?? 0,
      label: "Converted",
      color: T.emerald,
      svg: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={T.emerald} strokeWidth="1.8"><polyline points="20 6 9 17 4 12"/></svg>,
    },
    {
      value: `${s.conversionRate ?? 0}%`,
      label: "Conversion Rate",
      color: T.violet,
      svg: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={T.violet} strokeWidth="1.8"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
    },
  ];

  const STAGE_LABELS = {
    new_lead:    { label: "New Leads",    color: T.sky    },
    interested:  { label: "Interested",   color: T.indigo },
    follow_up:   { label: "Follow Up",    color: T.amber  },
    proposal:    { label: "Proposal",     color: T.violet },
    negotiating: { label: "Negotiating",  color: T.rose   },
    my_client:   { label: "My Clients",   color: T.emerald},
    lost:        { label: "Lost",         color: T.text3  },
  };
  const totalPipeline = Object.values(stages).reduce((a, b) => a + b, 0);

  return (
    <>
      {/* Stat cards */}
      <div className="grid-4" style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 16, marginBottom: 24 }}>
        {statCards.map((sc, i) => <StatCard key={i} {...sc} delay={i * 0.07} />)}
      </div>

      <div className="grid-2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>
        <ConnectionCard status={status} qr={qr} sessionInfo={sessionInfo} onShowModal={onShowModal} onDisconnect={onDisconnect} />

        {/* Lead Gen Pipeline Funnel */}
        <Card>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.text1, marginBottom: 18, display: "flex", alignItems: "center", gap: 8 }}>
            <Icon name="pipeline" size={15} color={T.text3} /> Lead Gen Pipeline
          </div>
          {totalPipeline === 0 ? (
            <div style={{ textAlign: "center", padding: "28px 0", color: T.text3 }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="1.5" style={{ opacity: 0.3, marginBottom: 8 }}><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
              <div style={{ fontSize: 13 }}>No leads yet</div>
              <div style={{ fontSize: 12, marginTop: 4, lineHeight: 1.6 }}>Pipeline data will appear here as the AI classifies contacts</div>
            </div>
          ) : (
            Object.entries(STAGE_LABELS).map(([key, { label, color }]) =>
              (stages[key] ?? 0) > 0 && (
                <FunnelBar key={key} label={label} count={stages[key] ?? 0} total={totalPipeline} color={color} />
              )
            )
          )}
        </Card>
      </div>

      {/* Recent activity & Broadcasts */}
      <div className="grid-2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>
        {/* Recent conversations */}
        <Card>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.text1, marginBottom: 18, display: "flex", alignItems: "center", gap: 8 }}>
            <Icon name="chats" size={15} color={T.text3} /> Recent Conversations
          </div>
          {!chats.length ? (
            <div style={{ textAlign: "center", padding: "24px 0" }}>
              <div style={{ marginBottom: 8, opacity: 0.2 }}><svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="1.5"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg></div>
              <div style={{ color: T.text3, fontSize: 13 }}>No conversations yet</div>
            </div>
          ) : chats.slice(0, 6).map((c, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "9px 0", borderBottom: i < Math.min(chats.length, 6) - 1 ? `1px solid ${T.border1}` : "none" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 34, height: 34, borderRadius: 10, background: "linear-gradient(135deg,rgba(99,102,241,0.2),rgba(56,189,248,0.08))", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: T.emerald }}>
                  {(c.contactName || cleanPhone(c.contact))[0]?.toUpperCase()}
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500, color: T.text1 }}>{c.contactName || cleanPhone(c.contact)}</div>
                  <div style={{ fontSize: 11, color: T.text3, maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginTop: 1 }}>{c.lastMessage || "—"}</div>
                </div>
              </div>
              <div style={{ textAlign: "right", flexShrink: 0, marginLeft: 8 }}>
                <div style={{ fontSize: 10, color: T.text3 }}>{timeAgo(c.lastMessageAt)}</div>
                {c.isHumanMode && <Badge label="HUMAN" color="amber" />}
                {c.unreadCount > 0 && !c.isHumanMode && <Badge label={String(c.unreadCount)} color="emerald" />}
              </div>
            </div>
          ))}
        </Card>

        {/* Recent broadcasts */}
        <Card>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.text1, marginBottom: 18, display: "flex", alignItems: "center", gap: 8 }}>
            <Icon name="broadcast" size={15} color={T.text3} /> Recent Broadcasts
          </div>
          {!broadcasts.length ? (
            <div style={{ textAlign: "center", padding: "24px 0", color: T.text3 }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="1.5" style={{ opacity: 0.3, marginBottom: 8 }}><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.22C1.6 2.1 2.43 1.18 3.55 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.91a16 16 0 0 0 6 6l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21.73 16.92z"/></svg>
              <div style={{ fontSize: 13 }}>No broadcasts yet</div>
            </div>
          ) : broadcasts.slice(0, 4).map((b, i) => {
            const sc = { completed: "emerald", running: "amber", failed: "rose", draft: "sky" };
            return (
              <div key={i} style={{ padding: "10px 0", borderBottom: i < Math.min(broadcasts.length, 4) - 1 ? `1px solid ${T.border1}` : "none" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: T.text1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 160 }}>{b.name}</div>
                  <Badge label={b.status} color={sc[b.status] || "sky"} />
                </div>
                <div style={{ fontSize: 12, color: T.text3, display: "flex", gap: 10 }}>
                  <span>{b.recipients?.length || 0} recipients</span>
                  <span style={{ color: T.indigo }}>{b.sentCount || 0} sent</span>
                  {b.failedCount > 0 && <span style={{ color: T.rose }}>{b.failedCount} failed</span>}
                </div>
              </div>
            );
          })}
        </Card>
      </div>

    </>
  );
};

// ─────────────────────────────────────────────────────────────────
// E-COM MODE — Overview
// ─────────────────────────────────────────────────────────────────
const EcomOverview = ({ overviewStats, chats, status, qr, sessionInfo, onShowModal, onDisconnect }) => {
  const s = overviewStats || {};
  const stages = s.stages || {};

  const currencyLabel = s.currency ? `${s.currency} ` : "";

  const ECOM_STAGE_CONFIG = {
    new_order:            { label: "New Orders",          color: T.sky    },
    pending_confirmation: { label: "Pending Confirm",     color: T.amber  },
    confirmed:            { label: "Confirmed",           color: T.emerald},
    follow_up:            { label: "Follow Up Sent",      color: T.indigo },
    cancelled:            { label: "Cancelled",           color: T.rose   },
  };

  const statCards = [
    {
      value: s.todayOrders ?? 0,
      label: "Orders Today",
      color: T.sky,
      svg: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={T.sky} strokeWidth="1.8"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></svg>,
    },
    {
      value: s.totalOrders ?? 0,
      label: "Total Orders",
      color: T.indigo,
      svg: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={T.indigo} strokeWidth="1.8"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>,
    },
    {
      value: `${s.confirmRate ?? 0}%`,
      label: "Confirm Rate",
      color: T.emerald,
      svg: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={T.emerald} strokeWidth="1.8"><polyline points="20 6 9 17 4 12"/></svg>,
    },
    {
      value: s.revenue != null ? `${currencyLabel}${parseFloat(s.revenue).toLocaleString()}` : "—",
      label: "Revenue (Confirmed)",
      color: T.violet,
      svg: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={T.violet} strokeWidth="1.8"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>,
    },
  ];

  return (
    <>
      {/* Stat cards */}
      <div className="grid-4" style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 16, marginBottom: 24 }}>
        {statCards.map((sc, i) => <StatCard key={i} {...sc} delay={i * 0.07} />)}
      </div>

      <div className="grid-2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>
        <ConnectionCard status={status} qr={qr} sessionInfo={sessionInfo} onShowModal={onShowModal} onDisconnect={onDisconnect} />

        {/* E-com Pipeline Stages */}
        <Card>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.text1, marginBottom: 18, display: "flex", alignItems: "center", gap: 8 }}>
            <Icon name="pipeline" size={15} color={T.text3} /> Order Pipeline
          </div>
          {Object.values(stages).every(v => v === 0) || !s.totalOrders ? (
            <div style={{ textAlign: "center", padding: "28px 0", color: T.text3 }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="1.5" style={{ opacity: 0.3, marginBottom: 8 }}><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/></svg>
              <div style={{ fontSize: 13 }}>No orders yet</div>
              <div style={{ fontSize: 12, marginTop: 4, lineHeight: 1.6 }}>Orders will appear here once your store sends webhooks</div>
            </div>
          ) : (
            Object.entries(ECOM_STAGE_CONFIG).map(([key, { label, color }]) => (
              <FunnelBar key={key} label={label} count={stages[key] ?? 0} total={s.totalOrders || 1} color={color} />
            ))
          )}
        </Card>
      </div>

      {/* Recent orders + Recent chats */}
      <div className="grid-2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>
        {/* Recent orders */}
        <Card>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.text1, marginBottom: 18, display: "flex", alignItems: "center", gap: 8 }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="1.8"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></svg>
            Recent Orders
          </div>
          {!s.recentOrders?.length ? (
            <div style={{ textAlign: "center", padding: "24px 0", color: T.text3, fontSize: 13 }}>No orders yet</div>
          ) : s.recentOrders.map((o, i) => {
            const statusColor = { confirmed: "emerald", pending: "amber", cancelled: "rose", no_response: "sky" };
            return (
              <div key={i} style={{ padding: "10px 0", borderBottom: i < s.recentOrders.length - 1 ? `1px solid ${T.border1}` : "none" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 3 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: T.text1 }}>
                    #{o.orderId} · {o.orderData?.customerName || "Unknown"}
                  </div>
                  <Badge label={o.confirmationStatus} color={statusColor[o.confirmationStatus] || "sky"} />
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: T.text3 }}>
                  <span style={{ textTransform: "capitalize" }}>{o.platform}</span>
                  <span style={{ fontFamily: base.mono, color: T.emerald }}>{o.orderData?.total || "—"}</span>
                  <span>{timeAgo(o.createdAt)}</span>
                </div>
              </div>
            );
          })}
        </Card>

        {/* Recent conversations */}
        <Card>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.text1, marginBottom: 18, display: "flex", alignItems: "center", gap: 8 }}>
            <Icon name="chats" size={15} color={T.text3} /> Recent Chats
          </div>
          {!chats.length ? (
            <div style={{ textAlign: "center", padding: "24px 0", color: T.text3, fontSize: 13 }}>No conversations yet</div>
          ) : chats.slice(0, 6).map((c, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "9px 0", borderBottom: i < Math.min(chats.length, 6) - 1 ? `1px solid ${T.border1}` : "none" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 34, height: 34, borderRadius: 10, background: "linear-gradient(135deg,rgba(99,102,241,0.2),rgba(56,189,248,0.08))", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: T.emerald }}>
                  {(c.contactName || cleanPhone(c.contact))[0]?.toUpperCase()}
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500, color: T.text1 }}>{c.contactName || cleanPhone(c.contact)}</div>
                  <div style={{ fontSize: 11, color: T.text3, maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginTop: 1 }}>{c.lastMessage || "—"}</div>
                </div>
              </div>
              <div style={{ textAlign: "right", flexShrink: 0, marginLeft: 8 }}>
                <div style={{ fontSize: 10, color: T.text3 }}>{timeAgo(c.lastMessageAt)}</div>
                {c.isHumanMode && <Badge label="HUMAN" color="amber" />}
                {c.unreadCount > 0 && !c.isHumanMode && <Badge label={String(c.unreadCount)} color="emerald" />}
              </div>
            </div>
          ))}
        </Card>
      </div>

    </>
  );
};

// ── Handoff notification numbers card (up to 3) ───────────────────
const NotificationCard = ({ token, showToast }) => {
  const [numbers,  setNumbers]  = useState([""]);
  const [enabled,  setEnabled]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [toggling, setToggling] = useState(false);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    apiFetch("/api/bot-settings", {}, token)
      .then(d => {
        const s = d.settings || {};
        if (Array.isArray(s.notificationNumbers) && s.notificationNumbers.length) {
          setNumbers(s.notificationNumbers.concat(Array(Math.max(0, 1 - s.notificationNumbers.length)).fill("")));
        } else if (s.notificationNumber) {
          setNumbers([s.notificationNumber]);
        } else {
          setNumbers([""]);
        }
        setEnabled(s.notificationsEnabled !== false);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  const save = async () => {
    setSaving(true);
    try {
      const cleaned = numbers.map(n => n.replace(/\D/g, "")).filter(Boolean);
      const r = await apiFetch("/api/bot-settings", {
        method: "POST",
        body: JSON.stringify({ notificationNumbers: cleaned }),
      }, token);
      if (r.success) showToast?.("Notification numbers saved");
      else showToast?.(r.error || "Save failed", "error");
    } catch { showToast?.("Network error", "error"); }
    setSaving(false);
  };

  const toggleEnabled = async () => {
    const next = !enabled;
    setToggling(true);
    try {
      const r = await apiFetch("/api/bot-settings", {
        method: "POST",
        body: JSON.stringify({ notificationsEnabled: next }),
      }, token);
      if (r.success) {
        setEnabled(next);
        showToast?.(next ? "Handoff notifications enabled" : "Handoff notifications disabled");
      } else {
        showToast?.(r.error || "Failed to update", "error");
      }
    } catch { showToast?.("Network error", "error"); }
    setToggling(false);
  };

  const setNum = (i, val) => setNumbers(prev => prev.map((n, idx) => idx === i ? val.replace(/\D/g, "") : n));
  const addNum = () => { if (numbers.length < 3) setNumbers(prev => [...prev, ""]); };
  const removeNum = (i) => setNumbers(prev => prev.length > 1 ? prev.filter((_, idx) => idx !== i) : [""]);

  return (
    <Card style={{ marginTop: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
        <div style={{ width: 34, height: 34, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", background: enabled ? "rgba(99,102,241,0.12)" : T.bg0, border: `1px solid ${enabled ? "rgba(99,102,241,0.2)" : T.border1}`, transition: "all 0.2s" }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={enabled ? T.indigo : T.text3} strokeWidth="1.8" style={{ transition: "stroke 0.2s" }}><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.text1 }}>Handoff Notifications</div>
          <div style={{ fontSize: 11, color: T.text3, marginTop: 1 }}>Up to 3 WhatsApp numbers alerted on every handoff</div>
        </div>
        {/* Master on/off toggle */}
        <button
          onClick={toggleEnabled}
          disabled={toggling || loading}
          title={enabled ? "Disable handoff notifications" : "Enable handoff notifications"}
          style={{
            display: "flex", alignItems: "center", gap: 7,
            padding: "7px 14px", borderRadius: 20,
            cursor: toggling || loading ? "not-allowed" : "pointer",
            background: enabled ? "rgba(16,185,129,0.12)" : T.bg0,
            border: `1px solid ${enabled ? "rgba(16,185,129,0.3)" : T.border1}`,
            color: enabled ? T.emerald : T.text3,
            fontSize: 12, fontWeight: 700, fontFamily: "inherit",
            opacity: toggling ? 0.6 : 1,
            transition: "all 0.2s",
            flexShrink: 0,
          }}
        >
          {toggling
            ? <div style={{ width: 12, height: 12, border: `2px solid ${enabled ? T.emerald : T.text3}44`, borderTopColor: enabled ? T.emerald : T.text3, borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
            : (
              <div style={{ width: 28, height: 16, borderRadius: 8, background: enabled ? T.emerald : T.border1, position: "relative", transition: "background 0.2s", flexShrink: 0 }}>
                <div style={{ position: "absolute", top: 2, left: enabled ? 14 : 2, width: 12, height: 12, borderRadius: "50%", background: "#fff", transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.3)" }} />
              </div>
            )
          }
          {enabled ? "ON" : "OFF"}
        </button>
      </div>

      {loading ? (
        <div style={{ height: 42, borderRadius: 10, background: T.bg0, border: `1px solid ${T.border1}`, animation: "pulse 1.5s ease infinite" }} />
      ) : !enabled ? (
        <div style={{ padding: "14px 16px", borderRadius: 10, background: T.bg0, border: `1px solid ${T.border1}`, fontSize: 12, color: T.text3, textAlign: "center", lineHeight: 1.6 }}>
          Handoff notifications are disabled. Toggle ON to resume alerts.
        </div>
      ) : (
        <>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {numbers.map((num, i) => (
              <div key={i} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <div style={{ position: "relative", flex: 1 }}>
                  <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", fontSize: 13, color: T.text3, pointerEvents: "none" }}>+</span>
                  <input
                    type="tel"
                    placeholder={`923001234567${i > 0 ? ` (${i + 1})` : ""}`}
                    value={num}
                    onChange={e => setNum(i, e.target.value)}
                    style={{
                      width: "100%", boxSizing: "border-box",
                      padding: "10px 14px 10px 26px", borderRadius: 10,
                      background: T.bg0, border: `1px solid ${T.border1}`,
                      color: T.text1, fontSize: 13, fontFamily: "monospace",
                      outline: "none", transition: "border-color 0.15s",
                    }}
                    onFocus={e => (e.target.style.borderColor = T.indigo)}
                    onBlur={e  => (e.target.style.borderColor = T.border1)}
                  />
                </div>
                {numbers.length > 1 && (
                  <button
                    onClick={() => removeNum(i)}
                    style={{
                      width: 34, height: 34, borderRadius: 8, border: `1px solid ${T.border1}`,
                      background: T.bg0, cursor: "pointer", color: T.text3,
                      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                    }}
                    title="Remove"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                )}
              </div>
            ))}

            {numbers.length < 3 && (
              <button
                onClick={addNum}
                style={{
                  padding: "8px 14px", borderRadius: 9, border: `1px dashed ${T.border1}`,
                  background: "none", cursor: "pointer", color: T.text3, fontSize: 12,
                  fontFamily: "inherit", display: "flex", alignItems: "center", gap: 6,
                  transition: "all 0.15s",
                }}
                onMouseOver={e => { e.currentTarget.style.borderColor = T.indigo; e.currentTarget.style.color = T.indigo; }}
                onMouseOut={e  => { e.currentTarget.style.borderColor = T.border1; e.currentTarget.style.color = T.text3; }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                Add number ({numbers.length}/3)
              </button>
            )}
          </div>

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 12, gap: 10, flexWrap: "wrap" }}>
            <div style={{ fontSize: 11, color: T.text3, lineHeight: 1.6 }}>
              {numbers.filter(n => n).length > 0
                ? <>Alerts → <span style={{ color: T.indigo, fontWeight: 600, fontFamily: "monospace" }}>{numbers.filter(n => n).map(n => `+${n}`).join(", ")}</span></>
                : "Include country code, no spaces — e.g. 923001234567"}
            </div>
            <button
              onClick={save}
              disabled={saving}
              style={{
                padding: "9px 18px", borderRadius: 10, border: "none", cursor: saving ? "not-allowed" : "pointer",
                background: T.indigo, color: "#fff", fontSize: 13, fontWeight: 600,
                opacity: saving ? 0.7 : 1, whiteSpace: "nowrap", fontFamily: "inherit",
                display: "flex", alignItems: "center", gap: 6, flexShrink: 0,
              }}
            >
              {saving
                ? <><div style={{ width: 12, height: 12, border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "#fff", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} /> Saving</>
                : "Save"}
            </button>
          </div>
        </>
      )}
    </Card>
  );
};

// ─────────────────────────────────────────────────────────────────
// Main export
// ─────────────────────────────────────────────────────────────────
export default function OverviewPage({ status, qr, onDisconnect, chats, broadcasts, onShowModal, sessionInfo, maintenance, announcement, planInfo, credits, setPage, overviewStats: overviewStatsProp, hasEcomStore, token, onLimitHit, showToast }) {
  const isEcom = hasEcomStore || overviewStatsProp?.mode === "ecom";

  // ── Date filter state ─────────────────────────────────────────────
  const [datePreset,   setDatePreset]   = useState("all");
  const [filteredStats, setFilteredStats] = useState(null);
  const [statsLoading,  setStatsLoading]  = useState(false);

  const overviewStats = filteredStats ?? overviewStatsProp;

  const fetchFilteredStats = useCallback(async (preset) => {
    if (preset === "all") { setFilteredStats(null); return; }
    setStatsLoading(true);
    try {
      const { dateFrom, dateTo } = presetToRange(preset);
      const params = new URLSearchParams();
      if (dateFrom) params.set("dateFrom", dateFrom);
      if (dateTo)   params.set("dateTo",   dateTo);
      const d = await apiFetch(`/api/overview-stats?${params}`, {}, token);
      setFilteredStats(d);
    } catch { /* fall back to prop */ setFilteredStats(null); }
    setStatsLoading(false);
  }, [token]);

  const handlePresetChange = (preset) => {
    setDatePreset(preset);
    fetchFilteredStats(preset);
  };

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "clamp(14px, 4vw, 26px) clamp(14px, 4vw, 28px) 50px" }}>

      {/* Maintenance banner */}
      {maintenance && (
        <div style={{
          marginBottom: 20, padding: "14px 18px", borderRadius: 14,
          background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.3)",
          display: "flex", alignItems: "flex-start", gap: 12, animation: "fadeUp 0.3s ease",
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.amber} strokeWidth="2" style={{flexShrink:0,marginTop:1}}><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#f59e0b", marginBottom: 2 }}>
              System Maintenance — Bot replies are paused for all users
            </div>
            {announcement && (
              <div style={{ fontSize: 12, color: "#f59e0b", opacity: 0.8, marginTop: 4, lineHeight: 1.6 }}>{announcement}</div>
            )}
          </div>
        </div>
      )}

      {/* Mode badge + Date filter */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, gap: 12, flexWrap: "wrap" }}>
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          padding: "4px 12px", borderRadius: 20, fontSize: 11, fontWeight: 700,
          background: isEcom ? "rgba(99,102,241,0.12)" : "rgba(16,185,129,0.10)",
          color: isEcom ? T.indigo : T.emerald,
          border: `1px solid ${isEcom ? T.indigo : T.emerald}33`,
        }}>
          {isEcom
            ? <><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/></svg> E-Commerce</>
            : <><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg> Lead Gen</>
          }
        </div>

        {/* Date range filter */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {statsLoading && (
            <div style={{ width: 14, height: 14, border: `2px solid ${T.border2}`, borderTopColor: T.indigo, borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
          )}
          <div style={{ position: "relative" }}>
            <svg style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            <select value={datePreset} onChange={e => handlePresetChange(e.target.value)}
              style={{ padding: "7px 30px 7px 28px", background: T.bg2, border: `1px solid ${T.border1}`, borderRadius: 10, color: datePreset === "all" ? T.text3 : T.text1, fontSize: 12, fontWeight: 600, outline: "none", cursor: "pointer", fontFamily: base.font, appearance: "none", WebkitAppearance: "none" }}>
              {DATE_PRESETS.map(p => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
            <svg style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg>
          </div>
        </div>
      </div>

      {isEcom
        ? <EcomOverview overviewStats={overviewStats} chats={chats} status={status} qr={qr} sessionInfo={sessionInfo} onShowModal={onShowModal} onDisconnect={onDisconnect} />
        : <LeadGenOverview overviewStats={overviewStats} chats={chats} broadcasts={broadcasts} status={status} qr={qr} sessionInfo={sessionInfo} onShowModal={onShowModal} onDisconnect={onDisconnect} />
      }

      <NotificationCard token={token} showToast={showToast} />
    </div>
  );
}
